/**
 * ProfileSubscriptionsEditor — SMS adapter for the shared
 * `@tiaki/profile-ui` `UserSubscriptionsEditor`.
 *
 * The shared editor is presentational + controlled: it renders the
 * protocol/alert/drill table and (for multi-site users) a site switcher, but
 * owns no data. This wrapper supplies everything app-specific:
 *   - the per-user subscription data hooks (`useUserSubscriptions` +
 *     the set/clear override mutations);
 *   - `canManage` / `canManageOwn`, read from the list response's
 *     `can_unsubscribe_own` / `can_manage_own` rather than a
 *     separate client-side permission check — this is the SELF surface, so
 *     the API's capability flags (which describe the requesting user) are
 *     exactly the write surface this editor offers;
 *   - the site-switcher selection state (the shared editor is controlled —
 *     `selectedSchoolId` + `onSchoolChange`), feeding the effective site to
 *     `useUserSubscriptions` so the backend `?school=` filter narrows the list;
 *   - the error toast (the lib has no toaster).
 *
 * The admin Users › edit surface uses the sibling `AdminSubscriptionsEditor`
 * for ON-BEHALF editing, which is unaffected by `can_manage_own` (that flag
 * governs self-service only) and keeps its own
 * `accounts.can_manage_subscription` check; the presentation lives in the lib.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  UserSubscriptionsEditor,
  type SiteOption,
  type UserSubscriptionOverridePayload,
} from '@tiaki/profile-ui';
import {
  useClearUserSubscriptionOverride,
  useSetUserSubscriptionOverride,
  useUserSubscriptions,
} from '../../hooks/queries/useUserSubscriptions';
import { useFeature } from '../../hooks/useFeature';
import { FeatureCode } from '../../types/features';
import { toaster } from '../ui/toaster';

interface ProfileSubscriptionsEditorProps {
  /** The user whose subscriptions are being edited (current user, self). */
  userId: number;
  /** The user's sites — the site-switcher candidates. */
  schools?: SiteOption[];
}

export function ProfileSubscriptionsEditor({
  userId,
  schools,
}: ProfileSubscriptionsEditorProps) {
  const { t } = useTranslation();
  // ADR-117 Mechanism B gate — page-level fallback only; each row's own
  // `escalation_enabled` (per-district) takes precedence in the
  // shared editor. Fails closed via `useFeature`.
  const escalationEnabled = useFeature(FeatureCode.ALERT_ESCALATION);

  // Site-downline scoping. The shared editor is controlled, so we
  // hold the selection here and resolve the effective site (first by default;
  // re-clamped if the selection falls outside the current site list) to feed
  // the data hook's `?school=` filter.
  const [selectedSchoolId, setSelectedSchoolId] = useState<number | null>(null);
  const effectiveSchoolId = useMemo(() => {
    if (!schools || schools.length === 0) return null;
    if (
      selectedSchoolId != null &&
      schools.some((s) => s.id === selectedSchoolId)
    ) {
      return selectedSchoolId;
    }
    return schools[0]?.id ?? null;
  }, [schools, selectedSchoolId]);

  const { data, isLoading } = useUserSubscriptions(userId, effectiveSchoolId);
  const setMut = useSetUserSubscriptionOverride(userId);
  const clearMut = useClearUserSubscriptionOverride(userId);

  const onError = (err: unknown) => {
    toaster.create({
      title: t(
        'profile.failedToUpdateSubscription',
        'Failed to update subscription',
      ),
      description: err instanceof Error ? err.message : undefined,
      type: 'error',
      duration: 4000,
    });
  };

  return (
    <UserSubscriptionsEditor
      rows={data?.results ?? []}
      loading={isLoading}
      isMutating={setMut.isPending || clearMut.isPending}
      canManage={data?.can_unsubscribe_own}
      canManageOwn={data?.can_manage_own}
      escalationEnabled={escalationEnabled}
      schools={schools}
      selectedSchoolId={effectiveSchoolId}
      onSchoolChange={setSelectedSchoolId}
      onSetOverride={(
        alertTypeId: number,
        body: UserSubscriptionOverridePayload,
      ) => setMut.mutate({ alertTypeId, body }, { onError })}
      onClearOverride={(alertTypeId: number) =>
        clearMut.mutate({ alertTypeId }, { onError })
      }
    />
  );
}

export default ProfileSubscriptionsEditor;
